export enum TrackingBasePrefix {
  BMW = 'bmw',
  AISF = 'aisf',
}

export enum TrackingClickPrefix {
  BTN = 'btn',
  TAB = 'tab',
  LINK = 'link',
  CONTENT = 'content',
  NAV = 'nav',
}

export enum TrackingModifierSuffix {
  DISABLED = '-in',
}

export const GA4_PROPERTY_MAX_LENGTH = 45 as const;
const MAX_PREFIX_LENGTH = 7 as const; // Length of "content"
const PREFIX_SEPARATOR_LENGTH = 1 as const; // Length of ":"
const DISABLED_SUFFIX_LENGTH = 3 as const; // Length of "-in"
const PROPERTY_MAX_LENGTH_WITHOUT_SUFFIX = 34 as const; // GA4_PROPERTY_MAX_LENGTH - MAX_PREFIX_LENGTH - PREFIX_SEPARATOR_LENGTH - DISABLED_SUFFIX_LENGTH

const COMPUTED_PROPERTY_MAX_LENGTH_WITHOUT_SUFFIX =
  GA4_PROPERTY_MAX_LENGTH - MAX_PREFIX_LENGTH - PREFIX_SEPARATOR_LENGTH - DISABLED_SUFFIX_LENGTH;

if (PROPERTY_MAX_LENGTH_WITHOUT_SUFFIX !== COMPUTED_PROPERTY_MAX_LENGTH_WITHOUT_SUFFIX) {
  throw new Error('Inconsistent analytics property max length without suffix');
}

export const ANALYTICS_PROPERTY_MAX_LENGTH = PROPERTY_MAX_LENGTH_WITHOUT_SUFFIX;
export type AnalyticsPropertyMaxLength = typeof PROPERTY_MAX_LENGTH_WITHOUT_SUFFIX;

export type AnalyticsPageNamePropertyMaxLength = 35; // GA4 max length - "aisf:" - "bmw:" (4) - 1 extra char for safety margin

export interface WebClickEvent {
  event: 'web_click';
  event_detail: {
    click_name: `${TrackingClickPrefix}:${string}`;
    click_id: string | null;
    click_text: string | null;
    click_url: string | null;
  };
}

export interface WebUploadEvent {
  event: 'web_upload';
  event_detail: {
    file_id: string;
    file_extension: string;
  };
}

export interface WebSearchEvent {
  event: 'web_search';
  event_detail: {
    search_location: string;
    search_results_count: number;
  };
}

export interface WebPageViewEvent {
  event: 'web_pv';
  context: {
    page_name: string;
    page_language: 'fr' | 'en';
    funnel_step: number | null;
    system_name: string;
    user_unique_id: string;
    user_role: 'advisor' | 'external-advisor' | 'assistant' | 'employee';
  };
  event_detail: {}; // Required by analytics plan
}

export interface WebFormStartedEvent {
  event: 'web_form_started';
  event_detail: {
    form_name: string;
    form_id: string;
  };
}

export interface WebFormSubmittedEvent {
  event: 'web_form_submitted';
  event_detail: {
    form_name: string;
    form_id: string;
  };
}

export interface WebViewContentEvent {
  event: 'web_view_content';
  event_detail: {
    content_name: string;
    content_detail: string | null;
  };
}

export interface UserRolePropertyEvent {
  event: 'user_role_property';
  event_detail: {
    user_role_property: 'advisor' | 'external-advisor' | 'assistant' | 'employee';
  };
}

export type AnalyticsEvent =
  | WebClickEvent
  | WebUploadEvent
  | WebSearchEvent
  | WebPageViewEvent
  | WebFormStartedEvent
  | WebFormSubmittedEvent
  | WebViewContentEvent
  | UserRolePropertyEvent;
